import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faClock, faTimes } from '@fortawesome/free-solid-svg-icons';
import { API_CONFIG, canEditWorkOrder } from '../config/api';

const EditWorkOrderForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    id_ustanova: '',
    id_aparat: '',
    datum: '',
    vrijeme_pocetka: '',
    vrijeme_zavrsetka: '',
    ukupno_radnih_sati: '',
    relacija: '',
    opis_kvara_intervencija: '',
    opis_poslova: '',
    napomena: '',
    interna_napomena: ''
  });
  const [ustanove, setUstanove] = useState([]);
  const [aparati, setAparati] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [canEdit, setCanEdit] = useState(true); // Default to true, check on load
  const [allDijelovi, setAllDijelovi] = useState([]);
  const [usedParts, setUsedParts] = useState([]); // {id_utroseni_dijelovi, id_dijelovi, kolicina}

  // Typeahead inputs
  const [ustanovaInput, setUstanovaInput] = useState('');
  const [showUstanovaOptions, setShowUstanovaOptions] = useState(false);
  const [aparatInput, setAparatInput] = useState('');
  const [showAparatOptions, setShowAparatOptions] = useState(false);

  // Time pickers
  const startTimeRef = useRef(null);
  const endTimeRef = useRef(null);
  const [showStartHour, setShowStartHour] = useState(false);
  const [showStartMinute, setShowStartMinute] = useState(false);
  const [showEndHour, setShowEndHour] = useState(false);
  const [showEndMinute, setShowEndMinute] = useState(false);
  const startPickerRef = useRef(null);
  const endPickerRef = useRef(null);
  const [timeWarning, setTimeWarning] = useState('');

  const HOURS = Array.from({ length: 24 }, (_, i) => String(i).padStart(2, '0'));
  const MINUTE_OPTIONS = Array.from({ length: 60 }, (_, i) => String(i).padStart(2, '0'));

  const loggedInUser = JSON.parse(localStorage.getItem('user'));
  const userId = loggedInUser?.id;

  useEffect(() => {
    const loadData = async () => {
      const token = localStorage.getItem('token');
      if (!token || !userId) {
        setError('Authentication error. Please log in again.');
        setLoading(false);
        return;
      }
      try {
        setLoading(true);
        const [ustanoveRes, aparatiRes, nalogRes, dijeloviRes] = await Promise.all([
          fetch(API_CONFIG.ENDPOINTS.USTANOVE, { headers: { 'Authorization': `Bearer ${token}` } }),
          fetch(API_CONFIG.ENDPOINTS.APARATI, { headers: { 'Authorization': `Bearer ${token}` } }),
          fetch(`${API_CONFIG.ENDPOINTS.NALOZI}/${id}`, { headers: { 'Authorization': `Bearer ${token}` } }),
          fetch(API_CONFIG.ENDPOINTS.DIJELOVI, { headers: { 'Authorization': `Bearer ${token}` } })
        ]);
        if (!ustanoveRes.ok || !aparatiRes.ok || !nalogRes.ok || !dijeloviRes.ok) {
          throw new Error('Ne mogu dohvatiti podatke za uređivanje.');
        }
        const [ustanoveData, aparatiData, nalogData, dijeloviData] = await Promise.all([
          ustanoveRes.json(), aparatiRes.json(), nalogRes.json(), dijeloviRes.json()
        ]);
        
        // Check if user can edit this work order
        const editPermission = await canEditWorkOrder(nalogData.user_id || nalogData.User?.id);
        setCanEdit(editPermission);
        
        if (!editPermission) {
          setError('Nemate dozvolu da editujete ovaj radni nalog.');
          setLoading(false);
          return;
        }
        setUstanove(ustanoveData);
        setAparati(aparatiData);
        setAllDijelovi(dijeloviData);

        setFormData({
          id_ustanova: nalogData.id_ustanova || '',
          id_aparat: nalogData.id_aparat || '',
          datum: nalogData.datum || '',
          vrijeme_pocetka: nalogData.vrijeme_pocetka || '',
          vrijeme_zavrsetka: nalogData.vrijeme_zavrsetka || '',
          ukupno_radnih_sati: nalogData.ukupno_radnih_sati || '',
          relacija: nalogData.relacija || '',
          opis_kvara_intervencija: nalogData.opis_kvara_intervencija || '',
          opis_poslova: nalogData.opis_poslova || '',
          napomena: nalogData.napomena || '',
          interna_napomena: nalogData.interna_napomena || ''
        });
        setUstanovaInput(nalogData.Ustanove?.naziv_ustanove || '');
        if (nalogData.Aparati) {
          setAparatInput(`${nalogData.Aparati.naziv_aparata} (${nalogData.Aparati.id_broj})`);
        }
        // Try to populate used parts from nalog response first (standardized alias)
        let initialUsedParts = Array.isArray(nalogData.utroseniDijelovi)
          ? nalogData.utroseniDijelovi
          : (Array.isArray(nalogData.UtroseniDijelovis)
            ? nalogData.UtroseniDijelovis
            : (Array.isArray(nalogData.UtroseniDijelovi) ? nalogData.UtroseniDijelovi : []));
        if (initialUsedParts.length > 0) {
          const mapped = initialUsedParts.map(u => ({
            id_utroseni_dijelovi: u.id_utroseni_dijelovi,
            id_dijelovi: String(u.id_dijelovi),
            kolicina: String(u.kolicina)
          }));
          setUsedParts(mapped);
        } else {
          // No used parts returned with the work order; start with an empty list.
          setUsedParts([]);
        }
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [id, userId]);

  useEffect(() => {
    if (formData.vrijeme_pocetka && formData.vrijeme_zavrsetka) {
      const start = new Date(`1970-01-01T${formData.vrijeme_pocetka}`);
      const end = new Date(`1970-01-01T${formData.vrijeme_zavrsetka}`);
      if (start > end) {
        setTimeWarning('Vrijeme početka ne može biti veće od vremena završetka.');
        setFormData(prev => ({ ...prev, ukupno_radnih_sati: '' }));
      } else if (end > start) {
        const diff = new Date(end.getTime() - start.getTime());
        const hours = diff.getUTCHours().toString().padStart(2, '0');
        const minutes = diff.getUTCMinutes().toString().padStart(2, '0');
        const seconds = diff.getUTCSeconds().toString().padStart(2, '0');
        setFormData(prev => ({ ...prev, ukupno_radnih_sati: `${hours}:${minutes}:${seconds}` }));
        setTimeWarning('');
      } else {
        setFormData(prev => ({ ...prev, ukupno_radnih_sati: '' }));
        setTimeWarning('');
      }
    } else {
      setTimeWarning('');
    }
  }, [formData.vrijeme_pocetka, formData.vrijeme_zavrsetka]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (startPickerRef.current && !startPickerRef.current.contains(event.target)) {
        setShowStartHour(false);
        setShowStartMinute(false);
      }
      if (endPickerRef.current && !endPickerRef.current.contains(event.target)) {
        setShowEndHour(false);
        setShowEndMinute(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const getTimeParts = (value) => {
    const [h, m] = (value || '').split(':');
    return { h: h?.padStart(2, '0') || '', m: m?.padStart(2, '0') || '' };
  };

  const setStartHour = (hour) => {
    const { m } = getTimeParts(formData.vrijeme_pocetka);
    const minutes = m || '00';
    setFormData(prev => ({ ...prev, vrijeme_pocetka: `${hour}:${minutes}` }));
  };
  const setStartMinute = (minute) => {
    const { h } = getTimeParts(formData.vrijeme_pocetka);
    const hour = h || '00';
    setFormData(prev => ({ ...prev, vrijeme_pocetka: `${hour}:${minute}` }));
    setShowStartMinute(false);
  };
  const setEndHour = (hour) => {
    const { m } = getTimeParts(formData.vrijeme_zavrsetka);
    const minutes = m || '00';
    setFormData(prev => ({ ...prev, vrijeme_zavrsetka: `${hour}:${minutes}` }));
  };
  const setEndMinute = (minute) => {
    const { h } = getTimeParts(formData.vrijeme_zavrsetka);
    const hour = h || '00';
    setFormData(prev => ({ ...prev, vrijeme_zavrsetka: `${hour}:${minute}` }));
    setShowEndMinute(false);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === 'id_ustanova') {
      setFormData({ ...formData, id_ustanova: value, id_aparat: '' });
      setAparatInput('');
      return;
    }
    setFormData({ ...formData, [name]: value });
  };

  const filteredUstanove = (ustanove || []).filter(u =>
    (u?.naziv_ustanove || '').toLowerCase().includes((ustanovaInput || '').toLowerCase())
  );

  const aparatiForUstanova = (aparati || []).filter(a =>
    String(a.id_ustanova) === String(formData.id_ustanova || '')
  );
  const filteredAparati = aparatiForUstanova.filter(a =>
    (`${a.naziv_aparata} ${a.id_broj}`).toLowerCase().includes((aparatInput || '').toLowerCase())
  );

  const normalizeToHHMMSS = (value) => {
    if (!value) return null;
    const trimmed = String(value).trim();
    if (/^\d{2}:\d{2}:\d{2}$/.test(trimmed)) return trimmed;
    if (/^\d{2}:\d{2}$/.test(trimmed)) return `${trimmed}:00`;
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (timeWarning) {
      alert(timeWarning);
      return;
    }
    // Manual validation for required fields
    const requiredFields = [
      'id_ustanova',
      'id_aparat',
      'datum',
      'vrijeme_pocetka',
      'vrijeme_zavrsetka',
      'opis_kvara_intervencija',
      'relacija',
      'opis_poslova'
    ];
    const missing = requiredFields.filter((f) => !String(formData[f] || '').trim());
    if (missing.length > 0) {
      alert('Molimo popunite sva obavezna polja (Ustanova, Aparat, Datum, Vrijeme početka, Vrijeme završetka, Relacija, Opis posla).');
      return;
    }
    const token = localStorage.getItem('token');
    try {
      // Prepare payload with correct types and time formats
      const payload = {
        ...formData,
        id_ustanova: formData.id_ustanova ? Number(formData.id_ustanova) : null,
        id_aparat: formData.id_aparat ? Number(formData.id_aparat) : null,
        vrijeme_pocetka: normalizeToHHMMSS(formData.vrijeme_pocetka),
        vrijeme_zavrsetka: normalizeToHHMMSS(formData.vrijeme_zavrsetka),
        ukupno_radnih_sati: normalizeToHHMMSS(formData.ukupno_radnih_sati),
        user_id: userId
      };

      // Prepare used parts payload for full update
      const fullUsedParts = usedParts
        .filter(r => r.id_dijelovi && r.kolicina)
        .map(r => ({
          id_utroseni_dijelovi: r.id_utroseni_dijelovi ? Number(r.id_utroseni_dijelovi) : undefined,
          id_dijelovi: Number(r.id_dijelovi),
          kolicina: Number(r.kolicina)
        }));

      const res = await fetch(`${API_CONFIG.ENDPOINTS.NALOZI}/${id}/full`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ ...payload, utroseniDijelovi: fullUsedParts }),
      });
      if (!res.ok) {
        let message = 'Neuspješno uređivanje naloga.';
        try {
          const errJson = await res.json();
          if (errJson && errJson.error) message = errJson.error;
        } catch (_) {}
        throw new Error(message);
      }
      alert('Nalog je uspješno uređen.');
      navigate(`/work-orders/${id}`);
    } catch (err) {
      setError(err.message);
    }
  };

  // Used parts helpers
  const availableParts = (allDijelovi || []).filter(d => Number(d.dostupna_kolicina) >= 0);

  const addUsedPartRow = () => {
    setUsedParts(prev => [...prev, { id_utroseni_dijelovi: null, id_dijelovi: '', kolicina: '' }]);
  };

  const updateUsedPartField = (index, field, value) => {
    setUsedParts(prev => prev.map((row, i) => i === index ? { ...row, [field]: value } : row));
  };

  const deleteUsedPartRow = (index) => {
    // Lokalno uklanjanje; backend će se uskladiti na "Spremi promjene"
    setUsedParts(prev => prev.filter((_, i) => i !== index));
  };

  if (loading) return (
    <div className="flex items-center justify-center min-h-[50vh] text-gray-600">Učitavanje obrasca...</div>
  );
  if (error) return (
    <div className="max-w-3xl mx-auto p-4">
      <div className="p-3 mb-4 text-sm text-red-700 bg-red-100 rounded-lg">Greška: {error}</div>
      <Link to={`/work-orders/${id}`} className="text-blue-600 hover:underline">Natrag na detalje</Link>
    </div>
  );

  return (
    <div className="max-w-3xl mx-auto">
      {/* Header i akcije */}
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-2xl font-semibold text-gray-900">Uredi radni nalog #{id}</h2>
        <div className="flex gap-2">
          <Link to={`/work-orders/${id}`} className="px-3 py-2 rounded-md bg-gray-100 text-gray-800 hover:bg-gray-200">Odustani</Link>
        </div>
      </div>

      {/* FORM container - usklađen sa novim nalogom */}
      <form onSubmit={handleSubmit} className="bg-white rounded-xl shadow border border-gray-200 p-6 md:p-8 space-y-6">
        <div>
          <label className="block text-sm font-medium text-gray-700">Serviser</label>
          <div className="mt-1 text-gray-900 font-medium">{loggedInUser?.first_name} {loggedInUser?.last_name}</div>
        </div>

        {/* Ustanova - veći padding */}
        <div>
          <label className="block text-sm font-medium text-gray-700">Ustanova <span className="text-red-600">*</span></label>
          <div className="relative mt-1">
            <div className="flex items-center">
              <input
                type="text"
                value={ustanovaInput}
                onChange={(e) => { setUstanovaInput(e.target.value); setShowUstanovaOptions(true); }}
                onFocus={() => setShowUstanovaOptions(true)}
                placeholder="Počnite kucati za pretragu ustanova"
                className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 pr-10 px-3 py-3"
                aria-autocomplete="list"
              />
              {formData.id_ustanova && (
                <button type="button" title="Očisti" onClick={() => { setFormData({ ...formData, id_ustanova: '', id_aparat: '' }); setUstanovaInput(''); setAparatInput(''); }} className="-ml-9 mr-2 text-gray-500 hover:text-gray-700">
                  <FontAwesomeIcon icon={faTimes} />
                </button>
              )}
            </div>
            {showUstanovaOptions && (
              <ul
                className="absolute z-10 mt-1 max-h-56 w-full overflow-auto rounded-md border border-gray-200 bg-white shadow"
                onMouseDown={(e) => e.preventDefault()}
                onBlur={() => setTimeout(() => setShowUstanovaOptions(false), 100)}
              >
                {filteredUstanove.length === 0 && (
                  <li className="px-3 py-2 text-sm text-gray-500">Nema rezultata</li>
                )}
                {filteredUstanove.map(u => (
                  <li key={u.id_ustanova}>
                    <button
                      type="button"
                      className="w-full text-left px-3 py-2 hover:bg-gray-100"
                      onClick={() => {
                        setFormData({ ...formData, id_ustanova: u.id_ustanova, id_aparat: '' });
                        setUstanovaInput(u.naziv_ustanove);
                        setAparatInput('');
                        setShowUstanovaOptions(false);
                      }}
                    >
                      {u.naziv_ustanove}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
          <input type="hidden" name="id_ustanova" value={formData.id_ustanova} onChange={handleChange} required />
        </div>

        {/* Aparat - veći padding i disabled stil zadržan */}
        <div>
          <label className="block text-sm font-medium text-gray-700">Aparat <span className="text-red-600">*</span></label>
          <div className="relative mt-1">
            <div className="flex items-center">
              <input
                type="text"
                value={aparatInput}
                onChange={(e) => { setAparatInput(e.target.value); setShowAparatOptions(true); }}
                onFocus={() => setShowAparatOptions(true)}
                placeholder={!formData.id_ustanova ? "Prvo odaberite ustanovu" : "Pretraži aparate (naziv ili ID)"}
                className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 pr-10 disabled:bg-gray-50 disabled:text-gray-500 px-3 py-3"
                disabled={!formData.id_ustanova}
                aria-autocomplete="list"
              />
              {formData.id_aparat && (
                <button type="button" title="Očisti" onClick={() => { setFormData({ ...formData, id_aparat: '' }); setAparatInput(''); }} className="-ml-9 mr-2 text-gray-500 hover:text-gray-700">
                  <FontAwesomeIcon icon={faTimes} />
                </button>
              )}
            </div>
            {showAparatOptions && formData.id_ustanova && (
              <ul
                className="absolute z-10 mt-1 max-h-56 w-full overflow-auto rounded-md border border-gray-200 bg-white shadow"
                onMouseDown={(e) => e.preventDefault()}
                onBlur={() => setTimeout(() => setShowAparatOptions(false), 100)}
              >
                {filteredAparati.length === 0 && (
                  <li className="px-3 py-2 text-sm text-gray-500">Nema rezultata</li>
                )}
                {filteredAparati.map(a => (
                  <li key={a.id_aparat}>
                    <button
                      type="button"
                      className="w-full text-left px-3 py-2 hover:bg-gray-100"
                      onClick={() => {
                        setFormData({ ...formData, id_aparat: a.id_aparat });
                        setAparatInput(`${a.naziv_aparata} (${a.id_broj})`);
                        setShowAparatOptions(false);
                      }}
                    >
                      {a.naziv_aparata} ({a.id_broj})
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
          <input type="hidden" name="id_aparat" value={formData.id_aparat} onChange={handleChange} required />
        </div>

        {/* Datum - veći padding */}
        <div>
          <label className="block text-sm font-medium text-gray-700">Datum <span className="text-red-600">*</span></label>
          <input type="date" name="datum" value={formData.datum} onChange={handleChange} required className="mt-1 w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 px-3 py-3" />
        </div>

        {/* Vrijeme grid - veći padding na inputima */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700">Vrijeme početka <span className="text-red-600">*</span></label>
            <div ref={startPickerRef} className="relative mt-1">
              <input
                ref={startTimeRef}
                type="text"
                readOnly
                name="vrijeme_pocetka"
                value={formData.vrijeme_pocetka}
                className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 pr-10 px-3 py-3"
                placeholder="hh:mm"
                onClick={() => { setShowStartHour(true); setShowStartMinute(true); }}
                onFocus={() => { setShowStartHour(true); setShowStartMinute(true); }}
                required
              />
              <button type="button" onClick={() => { setShowStartHour(true); setShowStartMinute(true); }} className="absolute inset-y-0 right-0 px-3 text-gray-500 hover:text-gray-700">
                <FontAwesomeIcon icon={faClock} />
              </button>
              {showStartHour && (
                <ul className="absolute z-20 left-0 mt-2 w-24 max-h-56 overflow-auto rounded-md border border-gray-200 bg-white shadow">
                  {HOURS.map(h => (
                    <li key={h}>
                      <button type="button" className="w-full px-3 py-1.5 text-left hover:bg-gray-100" onClick={() => setStartHour(h)}>{h}</button>
                    </li>
                  ))}
                </ul>
              )}
              {showStartMinute && (
                <ul className="absolute z-20 right-0 mt-2 w-24 max-h-56 overflow-auto rounded-md border border-gray-200 bg-white shadow">
                  {MINUTE_OPTIONS.map(m => (
                    <li key={m}>
                      <button type="button" className="w-full px-3 py-1.5 text-left hover:bg-gray-100" onClick={() => { setStartMinute(m); setShowStartHour(false); }}>
                        {m}
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Vrijeme završetka <span className="text-red-600">*</span></label>
            <div ref={endPickerRef} className="relative mt-1">
              <input
                ref={endTimeRef}
                type="text"
                readOnly
                name="vrijeme_zavrsetka"
                value={formData.vrijeme_zavrsetka}
                className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 pr-10 px-3 py-3"
                placeholder="hh:mm"
                onClick={() => { setShowEndHour(true); setShowEndMinute(true); }}
                onFocus={() => { setShowEndHour(true); setShowEndMinute(true); }}
                required
              />
              <button type="button" onClick={() => { setShowEndHour(true); setShowEndMinute(true); }} className="absolute inset-y-0 right-0 px-3 text-gray-500 hover:text-gray-700">
                <FontAwesomeIcon icon={faClock} />
              </button>
              {showEndHour && (
                <ul className="absolute z-20 left-0 mt-2 w-24 max-h-56 overflow-auto rounded-md border border-gray-200 bg-white shadow">
                  {HOURS.map(h => (
                    <li key={h}>
                      <button type="button" className="w-full px-3 py-1.5 text-left hover:bg-gray-100" onClick={() => setEndHour(h)}>{h}</button>
                    </li>
                  ))}
                </ul>
              )}
              {showEndMinute && (
                <ul className="absolute z-20 right-0 mt-2 w-24 max-h-56 overflow-auto rounded-md border border-gray-200 bg-white shadow">
                  {MINUTE_OPTIONS.map(m => (
                    <li key={m}>
                      <button type="button" className="w-full px-3 py-1.5 text-left hover:bg-gray-100" onClick={() => { setEndMinute(m); setShowEndHour(false); }}>
                        {m}
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Ukupno radnih sati</label>
            <input type="text" name="ukupno_radnih_sati" value={formData.ukupno_radnih_sati} readOnly className="mt-1 w-full rounded-md bg-gray-50 border-gray-200 shadow-sm px-3 py-3" />
          </div>
        </div>

        {timeWarning && (
          <div className="text-sm text-red-600">{timeWarning}</div>
        )}

        {/* Detalji intervencije - usklađeni padding/rows */}
        <div>
          <label className="block text-sm font-medium text-gray-700">Relacija <span className="text-red-600">*</span></label>
          <input type="text" name="relacija" value={formData.relacija} onChange={handleChange} required className="mt-1 w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 px-3 py-3" />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700">Opis kvara/intervencija <span className="text-red-600">*</span></label>
          <textarea name="opis_kvara_intervencija" value={formData.opis_kvara_intervencija} onChange={handleChange} required className="mt-1 w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 p-3" rows="4"></textarea>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700">Opis posla <span className="text-red-600">*</span></label>
          <textarea name="opis_poslova" value={formData.opis_poslova} onChange={handleChange} required className="mt-1 w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 p-3" rows="4"></textarea>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700">Napomena</label>
          <textarea name="napomena" value={formData.napomena} onChange={handleChange} className="mt-1 w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 p-3" rows="3"></textarea>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700">Interna napomena</label>
          <textarea name="interna_napomena" value={formData.interna_napomena} onChange={handleChange} className="mt-1 w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 p-3" rows="2"></textarea>
        </div>

        <h3 className="text-lg font-medium text-gray-900">Utrošeni dijelovi</h3>
        <div className="bg-gray-50 border border-gray-200 rounded-md p-4 space-y-3">
          {usedParts.map((row, index) => (
            <div key={index} className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3">
              <select
                value={row.id_dijelovi}
                onChange={(e) => updateUsedPartField(index, 'id_dijelovi', e.target.value)}
                className="flex-1 rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 px-3 py-3"
              >
                <option value="">-- Odaberite dio --</option>
                {availableParts.map(dio => (
                  <option key={dio.id_dijelovi} value={String(dio.id_dijelovi)}>
                    {dio.naziv_dijela} (na stanju: {dio.dostupna_kolicina})
                  </option>
                ))}
              </select>
              <input
                type="number"
                min="1"
                value={row.kolicina}
                onChange={(e) => updateUsedPartField(index, 'kolicina', e.target.value)}
                placeholder="Količina"
                className="w-28 rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 px-3 py-3"
              />
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => deleteUsedPartRow(index)}
                  className="px-3 py-2 rounded-md bg-gray-100 text-gray-800 hover:bg-gray-200 transition-colors"
                >
                  Ukloni
                </button>
              </div>
            </div>
          ))}
          <button
            type="button"
            onClick={addUsedPartRow}
            className="px-3 py-2 rounded-md bg-emerald-600 text-white hover:bg-emerald-700 transition-colors"
          >
            Dodaj dio
          </button>
        </div>
        <div className="pt-4">
          <button type="submit" className="px-4 py-2 rounded-md bg-blue-600 text-white hover:bg-blue-700">Spremi promjene</button>
        </div>
      </form>
    </div>
  );
};

export default EditWorkOrderForm;
